import { motion } from "framer-motion";
import { Link, useParams } from "react-router-dom";
import { useMemo } from "react";
import { FuelMenuNav } from "@/components/fuel-activity/FuelMenuNav";
import { FuelMonthSummaryCards } from "@/components/fuel-activity/FuelMonthSummaryCards";
import { FuelYearPicker } from "@/components/fuel-activity/FuelYearPicker";
import { PageHeader } from "@/components/ui/PageHeader";
import { ExplorerSkeleton } from "@/components/ui/PageSkeleton";
import { SafeImage } from "@/components/ui/SafeImage";
import { getFuelMonthSummaries, getFuelYearSummaries } from "@/lib/fuel-analytics";
import { useUserFuelActivity } from "@/lib/queries";
import { imageSrc, userDisplayName } from "@/lib/utils";

const pageVariants = {
  initial: { opacity: 0, y: 8 },
  animate: { opacity: 1, y: 0 },
  exit: { opacity: 0, y: -4 },
};

export function UserFuelSpendYearsPage() {
  const { userId = "" } = useParams();
  const { data, isLoading, error } = useUserFuelActivity(userId);
  const basePath = `/fuel-activity/users/${userId}/spend`;

  const user = data?.user ?? null;
  const name = user ? userDisplayName(user) : userId.slice(0, 8);
  const profilePhoto = user ? imageSrc(user.profilePhoto) : null;

  const yearSummaries = useMemo(() => {
    if (!data) return [];
    return getFuelYearSummaries(data.logs);
  }, [data]);

  const monthSummaries = useMemo(() => {
    if (!data) return [];
    return getFuelMonthSummaries(data.logs);
  }, [data]);

  return (
    <motion.div
      variants={pageVariants}
      initial="initial"
      animate="animate"
      exit="exit"
      transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
      className="space-y-6"
    >
      <Link
        to={`/fuel-activity/users/${userId}`}
        className="inline-flex text-sm font-semibold text-muted transition hover:text-foreground"
      >
        ← {name}
      </Link>

      <PageHeader
        title={`${name} · spend breakdown`}
        description="Browse this user's fuel spend by month or year."
        showGreeting={false}
        action={
          profilePhoto ? (
            <SafeImage
              src={profilePhoto}
              alt={name}
              width={48}
              height={48}
              className="h-12 w-12 rounded-full object-cover"
            />
          ) : (
            <div className="flex h-12 w-12 items-center justify-center rounded-full bg-accent-subtle text-sm font-bold text-accent-foreground">
              {name.charAt(0).toUpperCase()}
            </div>
          )
        }
      />

      <FuelMenuNav />

      {isLoading ? (
        <ExplorerSkeleton title="Spend breakdown" />
      ) : error || !data ? (
        <p className="text-sm text-red-600">Failed to load spend data.</p>
      ) : (
        <div className="space-y-8">
          <section className="space-y-4">
            <div>
              <p className="text-[11px] font-bold uppercase tracking-wider text-muted">By month</p>
              <p className="mt-0.5 text-sm text-muted">Select a month to open its full breakdown.</p>
            </div>
            <FuelMonthSummaryCards months={monthSummaries} basePath={basePath} />
          </section>

          <section className="space-y-4">
            <div>
              <p className="text-[11px] font-bold uppercase tracking-wider text-muted">By year</p>
              <p className="mt-0.5 text-sm text-muted">Select a year for the annual overview and monthly rollup.</p>
            </div>
            <FuelYearPicker years={yearSummaries} basePath={basePath} />
          </section>
        </div>
      )}
    </motion.div>
  );
}
